import React, { useState, useEffect } from "react";
import { fontFaceMoontime, headStyle, bgStyleBlue, fontFaceBarlow } from './shared.module.scss';
import { getYardiPropId } from '../utils/propertyMapUtil';
import { transformDeals, transformUnits, getFloorplanNameYardi } from '../utils/floorplanTransforms';

const OverlaySpecial = ({ title, des, date, cid }) => {
  const [active, setActive] = useState(true);
  const [showBanner, setShowBanner] = useState(false);
  const [mostAgedDealText, setMostAgedDealText] = useState('');

  const formatDescription = (description) => {
    // Replace && with <br> first to avoid conflict with & replacement
    let formatted = description.replace(/&&/g, '<br>');
    // Replace ##text## with <strong>text</strong>
    formatted = formatted.replace(/##(.*?)##/g, '<strong>$1</strong>');
    // Replace & with &amp;
    formatted = formatted.replace(/&/g, '&amp;<br>');
    return formatted;
  };

  const formatDescriptionForBanner = (description) => {
    // For banner, remove HTML and keep it simple
    let formatted = description.replace(/&&/g, ' ');
    formatted = formatted.replace(/##(.*?)##/g, '$1');
    formatted = formatted.replace(/&/g, ' ');
    return formatted;
  };

  const closeModal = () => {
    setActive(false);
    setShowBanner(true);
  };

  const closeBanner = () => {
    setShowBanner(false);
  };

  const expandBanner = () => {
    setShowBanner(false);
    setActive(true);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (active && window.scrollY > 100) { // Close modal when scrolled down 100px
        closeModal();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [active]);

  useEffect(() => {
    if (!cid) return;

    const fetchMostAgedDeal = async () => {
      try {
        // Fetch deals
        const dealsResponse = await fetch("https://redisut.mcklab.com/index.php?action=getDeals");
        const dealsData = await dealsResponse.json();
        let deals = [];
        if (dealsData.res) {
          deals = JSON.parse(dealsData.res);
          
          // Apply deal transformations using the utility
          deals = transformDeals(deals, cid);
        }

        // Get Yardi community ID and fetch units
        const yardi_cid = getYardiPropId(cid);
        let fetchPromises = [];

        // Handle multi-building communities (same logic as floorplans.js)
        if (yardi_cid == "beachfl1") {
          fetchPromises.push(
            fetch("https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=beachfl1").then(res => res.json()),
            fetch("https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=beachfl2").then(res => res.json()),
            fetch("https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=beachfl3").then(res => res.json())
          );
        } else if (yardi_cid == "willfla1") {
          fetchPromises.push(
            fetch("https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=willfla1").then(res => res.json()),
            fetch("https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=willfla2").then(res => res.json())
          );
        } else if (yardi_cid == "golfla12") {
          fetchPromises.push(
            fetch("https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=golfla12").then(res => res.json()),
            fetch("https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=golfla03").then(res => res.json())
          );
        } else if (yardi_cid == "sc1") {
          fetchPromises.push(
            fetch("https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=sc1").then(res => res.json()),
            fetch("https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=sc2").then(res => res.json()),
            fetch("https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=sc3").then(res => res.json())
          );
        } else if (yardi_cid == "westfla1") {
          fetchPromises.push(
            fetch("https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=westfla1").then(res => res.json()),
            fetch("https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=westfla2").then(res => res.json())
          );
        } else if (yardi_cid == "mcdonald") {
          fetchPromises.push(
            fetch("https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=mcdonald").then(res => res.json()),
            fetch("https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=gleno2").then(res => res.json())
          );
        } else {
          fetchPromises.push(
            fetch(`https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=${yardi_cid}`).then(res => res.json())
          );
        }

        const results = await Promise.all(fetchPromises);
        let units = results.reduce((acc, result) => {
          if (result.res) {
            acc.push(...JSON.parse(result.res));
          }
          return acc;
        }, []);

        // Apply community-specific unit transformations using the utility
        units = transformUnits(units, yardi_cid);

        // Parse unit data (same as floorplanTemplate.js)
        const avnow = ["Vacant Unrented Not Ready", "Vacant Unrented Ready"];
        const avsoon = ["Notice Unrented"];
        
        const parsedUnits = units.map(unit => ({
          unit: unit.unit,
          isVacant: avnow.includes(unit.status),
          status: unit.status,
          price: unit.rent1,
          extra: unit.position,
          amenities: unit.amenities,
          moveOutDate: unit.date1,
          otherDate: unit.date2,
          floorplanName: getFloorplanNameYardi(unit.amenities),
        }));

        // Find all vacant units
        const vacantUnits = parsedUnits.filter(unit => unit.isVacant);
        const noticeUnits = parsedUnits.filter(unit => avsoon.includes(unit.status));
        const allAvailableUnits = [...vacantUnits, ...noticeUnits];

        // Find units with deals
        const unitsWithDeals = allAvailableUnits.filter(unit => {
          const unitTrimmed = unit.unit.trim();
          const currentFpName = unit.floorplanName;
          
          return deals.some(deal => {
            const normalizedSdesc = deal.sdesc.replace(/ Plus/g, '+');
            return deal.unit === unitTrimmed && normalizedSdesc.includes(currentFpName);
          });
        });

        // Sort by moveOutDate to find most aged (oldest vacant)
        const sortedUnitsWithDeals = unitsWithDeals.sort((a, b) => {
          const dateA = new Date(a.moveOutDate || '1970-01-01');
          const dateB = new Date(b.moveOutDate || '1970-01-01');
          return dateA - dateB;
        });

        // Get the most aged unit with a deal
        if (sortedUnitsWithDeals.length > 0) {
          const mostAgedUnit = sortedUnitsWithDeals[0];
          const unitTrimmed = mostAgedUnit.unit.trim();
          
          // Find the deal text for this unit
          const dealForUnit = deals.find(deal => {
            const normalizedSdesc = deal.sdesc.replace(/ Plus/g, '+');
            return deal.unit === unitTrimmed && normalizedSdesc.includes(mostAgedUnit.floorplanName);
          });

          if (dealForUnit) {
            // Apply same deal text logic as unitDisplayCards.js
            let dealText = dealForUnit.sdesc1;
            if (dealForUnit.unit == '1962-202' && dealForUnit.sdesc1 == '100.00') {
              dealText = '1 Months Free - Wooded View!';
            }
            setMostAgedDealText(dealText);
          }
        }

      } catch (error) {
        console.error('Error fetching most aged deal:', error);
      }
    };

    fetchMostAgedDeal();
  }, [cid]);

  return (
    <>
      {/* Modal Popup */}
      <div className={active ? "modal is-active" : "modal"}>
        <div className="modal-background" onClick={closeModal}></div>
        <div className={bgStyleBlue + " modal-content"} style={{ textAlign: 'center', padding: '2rem', borderRadius: '10px', position: 'relative' }}>
          <h1 className={`${fontFaceMoontime} ${headStyle}`}>{title}</h1>
          <p className={`${fontFaceBarlow}`} dangerouslySetInnerHTML={{ __html: formatDescription(des) }} style={{ fontSize: '2rem' }}></p>
          <p className={fontFaceBarlow}><em>{date}</em></p>
          {/*{mostAgedDealText && (
            <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '5px' }}>
              <p className={fontFaceBarlow} style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#fff' }}>
                {mostAgedDealText}
              </p>
            </div>
          )*/}
          <button className="modal-close is-large" aria-label="close" onClick={closeModal} style={{ position: 'absolute', top: '0', right: '0' }}></button>
        </div>
      </div>

      {/* Sticky Banner */}
      {showBanner && (
        <div 
          className={bgStyleBlue}
          style={{ 
            position: 'fixed', 
            top: '102px', 
            left: '0', 
            right: '0', 
            zIndex: '20',
            padding: '0.75rem 1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            cursor: 'pointer'
          }}
          onClick={expandBanner}
        >
          <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <strong className={fontFaceBarlow} style={{ marginRight: '1rem', fontSize: '1.1rem' }}>
              {title}
            </strong>
            <span className={fontFaceBarlow} style={{ fontSize: '0.9rem' }}>
              {formatDescriptionForBanner(des)}  {/*{date}*/}
              {/*{mostAgedDealText && ` | ${mostAgedDealText}` */}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', marginRight: '1rem', opacity: 0.8 }}>
              Click to expand
            </span>
            <button 
              className="delete is-small" 
              onClick={(e) => { e.stopPropagation(); closeBanner(); }}
              aria-label="close banner"
            ></button>
          </div>
        </div>
      )}
    </>
  );
};

export default OverlaySpecial;
